import { MoreVert } from "@mui/icons-material";
import { Divider, IconButton } from "@mui/material";
import theme from "Theme/AppTheme";
import { AcxViewSqlDialog } from "components/UI/AcxViewSqlDialog";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import { downloadCSVFromBlob } from "components/CSVExport/utils";
import { ExportToCSVAsync } from "components/CSVExport/ExportToCSVAsync";
import React, { useEffect, useMemo, useState } from "react";
import { toPng } from "html-to-image";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { styleClsx } from "utils/styleClsx";
import TableCardStore from "./TableCardStore";
import { ChartPinToDashboardDialog } from "../ChartCard/ChartMenu/ChartPinToDashboardDialog";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import {
    ISignalsChartDefinition,
    SignalsObjectType,
} from "../../SignalsModels";
import { AuthStore } from "stores/AuthStore";
import SignalsPageStore from "../../Store/SignalsPageStore";
import { TableConfirmDelete } from "./TableConfirmDelete";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import ObjectConfigurationDrawer from "../ObjectConfiguration/ObjectConfigurationDrawer";
import { useObjectMenu } from "../useObjectMenu";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";

/** Resolves after React has committed pending updates and the browser has laid them out. */
const waitForPaint = () =>
    new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

type Props = {
    store: TableCardStore;
    chartContainerRef: React.RefObject<HTMLDivElement>;
    isValidData: boolean;
};

const TableMenu = observer(
    ({ store, chartContainerRef, isValidData }: Props) => {
        const currentDashboard = useCurrentDashboard();
        const authStore = useStore(AuthStore);
        const signalsPageStore = useStore(SignalsPageStore);
        const drawerStore = useStore(LayoutDrawerStore);
        const messageStore = useStore(MessageStore);
        const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
        const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
        const [pinDialogOpen, setPinDialogOpen] = useState(false);
        const [isExportingCsv, setIsExportingCsv] = useState(false);
        const [isSelectExportColumnsOpen, setIsSelectExportColumnsOpen] =
            useState(false);
        const isRefreshing = store.isRefreshing;

        const {
            menuAnchor,
            isMenuOpen,
            setIsMenuOpen,
            handleMenuButtonClick,
            handleMenuButtonKeyDown,
        } = useObjectMenu();

        const exportStrategy = store.config.capabilities.csvExport;
        const isExportableAsCsv = exportStrategy !== "none";
        const allowColumnSelection = store.allowColumnSelection;

        const showSql = !!store.sqlQuery && authStore.isUserUltra();

        const chartDefinition = useMemo(() => {
            return currentDashboard?.signalsObjects.find(
                (obj) =>
                    obj.id === store.id &&
                    (obj.objectType === SignalsObjectType.Table ||
                        obj.objectType === SignalsObjectType.Chart),
            );
        }, [currentDashboard?.signalsObjects, store.id]);

        const canDelete = store.isDeletable;
        const canConfigure = store.isEditable;

        const handleExportPng = async () => {
            if (!chartContainerRef.current) return;

            // The grid is virtualized and clipped to the card's height, so switch it to
            // render every row on the current page before capturing.
            store.setPngCaptureInProgress(true);
            const originalDisplays: string[] = [];
            let elementsToHide: NodeListOf<HTMLElement> | undefined;

            try {
                await waitForPaint();
                const container = chartContainerRef.current;
                if (!container) return;

                elementsToHide =
                    container.querySelectorAll<HTMLElement>(".no-export");
                elementsToHide.forEach((el, i) => {
                    originalDisplays[i] = el.style.display;
                    el.style.display = "none";
                });

                // Fonts are embedded: with the fallback font, cell content such as the
                // Change chip wraps and is clipped by the fixed row height.
                const dataUrl = await toPng(container);
                const link = document.createElement("a");
                link.download = `${store.title ?? "Signals-Table-Export"}.png`;
                link.href = dataUrl;
                link.click();
            } finally {
                elementsToHide?.forEach((el, i) => {
                    el.style.display = originalDisplays[i];
                });
                store.setPngCaptureInProgress(false);
            }
        };

        const handleExportCsv = async (selectedColumns?: string[]) => {
            setIsExportingCsv(true);
            try {
                const blob = await store.exportCsv(selectedColumns);
                if (!blob) {
                    messageStore.logError(
                        "Failed to export table data as CSV.",
                    );
                    return;
                }
                downloadCSVFromBlob(
                    blob,
                    `${store.title ?? "Signals-Table-Export"}.csv`,
                );
            } finally {
                setIsExportingCsv(false);
            }
            setIsMenuOpen(false);
        };

        const startExportCsv = async () => {
            if (!isExportableAsCsv) return;

            if (allowColumnSelection) {
                await store.loadExportableFields();
                if (store.exportableFieldNodes.length === 0) {
                    messageStore.logError(
                        "No exportable fields are available for this table.",
                    );
                    return;
                }
                setIsSelectExportColumnsOpen(true);
            } else {
                await handleExportCsv();
            }
            setIsMenuOpen(false);
        };

        // initial selections are equal to the table columns
        const initialExportColumns = useMemo(() => {
            const columns = store.dataGridStore?.columns ?? [];
            if (columns.length === 0) return new Set<string>();

            const columnFieldsLower = new Set(
                columns.map((f) => f.field.toLowerCase()),
            );

            const leafKeys = store.exportableFieldNodes.flatMap(
                (group) => group.children?.map((c) => c.key) ?? [],
            );

            return new Set(
                leafKeys.filter((f) => columnFieldsLower.has(f.toLowerCase())),
            );
        }, [store.exportableFieldNodes, store.dataGridStore?.columns]);

        const hasPinTargets = signalsPageStore.pinTargetDashboards.some(
            (dash) => dash.id !== currentDashboard?.id,
        );

        const pinDisabled = !chartDefinition || !hasPinTargets;

        const shouldHideMenu = currentDashboard?.isPresentationMode;

        useEffect(() => {
            if (!isRefreshing) return;
            setIsMenuOpen(false);
        }, [isRefreshing, setIsMenuOpen]);

        const openConfigDrawer = () => {
            drawerStore.closeAndResetDrawer();
            drawerStore.restoreDefaults();
            drawerStore.setOffsetPixels(defaultDrawerOffset);
            drawerStore.setCustomRootStyles({
                zIndex: theme.zIndex.appBar - 1,
            });
            drawerStore.setContentFactory(() => (
                <ObjectConfigurationDrawer store={store} />
            ));
            drawerStore.setOpen(true);
        };

        if (shouldHideMenu || !store.config.renderOptions.menu) {
            return null;
        }

        return (
            <>
                <IconButton
                    id={`${store.id}-menu`}
                    ref={menuAnchor}
                    style={styleClsx({
                        color: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.black.main,
                        border: isRefreshing ? "none" : "1px solid",
                        borderColor: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.gray.light,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.white.main,
                        cursor: isRefreshing ? "default" : "pointer",
                    })}
                    onClick={handleMenuButtonClick}
                    onKeyDown={handleMenuButtonKeyDown}
                    size="small"
                    disabled={isRefreshing}
                >
                    <MoreVert />
                </IconButton>
                <AcxMenuV2
                    anchorEl={menuAnchor.current}
                    open={isMenuOpen}
                    onClose={() => setIsMenuOpen(false)}
                    disableAutoFocusItem
                >
                    <AcxMenuItem
                        id={`${store.id}-refresh`}
                        onClick={() => {
                            store.runRefresh();
                            setIsMenuOpen(false);
                        }}
                        disabled={store.getTaskLoading("refresh")}
                    >
                        Refresh
                    </AcxMenuItem>
                    <AcxMenuItem
                        id={`${store.id}-pin-to-dashboard`}
                        onClick={() => {
                            setPinDialogOpen(true);
                            setIsMenuOpen(false);
                        }}
                        disabled={pinDisabled}
                    >
                        Pin to dashboard
                    </AcxMenuItem>
                    {canConfigure && (
                        <AcxMenuItem
                            id={`${store.id}-configure`}
                            onClick={() => {
                                openConfigDrawer();
                                setIsMenuOpen(false);
                            }}
                            disabled={isRefreshing}
                        >
                            Configure
                        </AcxMenuItem>
                    )}
                    {(isValidData || isExportableAsCsv || showSql) && (
                        <Divider style={{ margin: "0px" }} />
                    )}
                    {isValidData && (
                        <AcxMenuItem
                            id={`${store.id}-export-png`}
                            onClick={() => {
                                handleExportPng();
                                setIsMenuOpen(false);
                            }}
                            disabled={!isValidData}
                        >
                            Export as PNG
                        </AcxMenuItem>
                    )}
                    {isExportableAsCsv && (
                        <AcxMenuItem
                            id={`${store.id}-export-csv`}
                            onClick={startExportCsv}
                            disabled={isExportingCsv || !isValidData}
                        >
                            Export CSV
                        </AcxMenuItem>
                    )}
                    {showSql && (
                        <AcxMenuItem
                            id={`${store.id}-sql`}
                            onClick={() => {
                                setSqlDialogOpen(true);
                                setIsMenuOpen(false);
                            }}
                        >
                            View SQL
                        </AcxMenuItem>
                    )}
                    {canDelete && <Divider style={{ margin: "0px" }} />}
                    {canDelete && (
                        <AcxMenuItem
                            id={`${store.id}-delete`}
                            onClick={() => {
                                setDeleteDialogOpen(true);
                                setIsMenuOpen(false);
                            }}
                            sx={{ color: theme.palette.error.main }}
                        >
                            Delete card
                        </AcxMenuItem>
                    )}
                </AcxMenuV2>

                <ChartPinToDashboardDialog
                    open={pinDialogOpen}
                    onClose={() => setPinDialogOpen(false)}
                    chartDefinition={
                        chartDefinition as ISignalsChartDefinition | undefined
                    }
                    pinState={store}
                />

                <AcxViewSqlDialog
                    sqlStatements={[store.sqlQuery]}
                    isModalOpen={sqlDialogOpen}
                    onClose={() => setSqlDialogOpen(false)}
                />
                <TableConfirmDelete
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    store={store}
                />
                {allowColumnSelection && (
                    <ExportToCSVAsync
                        open={isSelectExportColumnsOpen}
                        onOpenChange={setIsSelectExportColumnsOpen}
                        fields={store.exportableFieldNodes}
                        onExport={async (columns) => {
                            await handleExportCsv(columns);
                            setIsSelectExportColumnsOpen(false);
                        }}
                        title="Select columns to export"
                        initialSelectedKeys={initialExportColumns}
                    />
                )}
            </>
        );
    },
);

export default TableMenu;
